/**
 * Loading for the rules presets page (`/admin/presets`, staff only). Every
 * read goes through the staff door, once per visit; `?fixtures=1` (dev and
 * preview only) swaps them for the samples in `fixtures.ts`. One untimed read
 * through the places pages' `useLoad`.
 */
import { fetchPlacesWithRooms, fetchPresets } from "@/data/presets-api";
import { fetchEventTemplates, fetchScheduledEvents } from "@/data/place-schedule-api";
import { usePortal } from "@/features/portal/portal-context";
import { useFixtureMode, useLoad, warnUnlessAborted, type Load } from "@/features/places/use-places";
import { PRESET_FIXTURES, sampleWhereUsed } from "@/features/presets/fixtures";
import { readPresetsPage, type PresetsPageData } from "@/features/presets/presets";

export function usePresets(): Load<PresetsPageData> {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const [load] = useLoad<PresetsPageData>(
    sampleMode,
    () => ({ presets: PRESET_FIXTURES, whereUsed: sampleWhereUsed() }),
    (signal) => {
      // The four reads' own signal: the page leaving stops them, and so does
      // the presets read failing (it fails the page).
      const reads = new AbortController();
      signal.addEventListener("abort", () => reads.abort());
      const viewer = { isSuperAdmin };
      const options = { signal: reads.signal };
      // A where-used read failing is said on the page as not read; the detail goes to the console.
      return readPresetsPage({
        presets: () => fetchPresets(viewer, options),
        places: () => fetchPlacesWithRooms(viewer, options).catch(warnUnlessAborted(reads.signal, "the places")),
        templates: () => fetchEventTemplates(viewer, options).catch(warnUnlessAborted(reads.signal, "the templates")),
        repeats: () => fetchScheduledEvents(viewer, options).catch(warnUnlessAborted(reads.signal, "the repeats")),
      }).catch((e: unknown) => {
        reads.abort();
        throw e;
      });
    },
    [isSuperAdmin],
    { failed: "Could not read the rules presets." },
  );
  return load;
}
